<template>
  <form-summary
    v-if="customer.id"
    ref="summary"

    :schema-generator="schemaGenerator"
    :payload-generator="payloadGenerator"

    :json="customer"
    :collapsed="collapsed"

    class="customers-summary"

    @initialized="initialize"
  />
</template>

<script>
  import { mapState } from 'vuex';

  import momentize from '@sdk/helpers/formatters/momentize';

  import FormSummary from '@cordel/components/Form/Summary.vue';

  import schemaGenerator from '@sdk/views/Customers/Form/schema';
  import payloadGenerator from '@sdk/views/Customers/Form/payload';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import preload from '@/helpers/mixins/preload';

  import {
    GENRE_ID,
    EDUCATION_ID,
    COMPANY_ACTIVITY_ID,
    STATE_OF_BIRTH_ID,
    RG_STATE_ID,
    CNH_TYPE_ID,
    MARITAL_STATUS_ID,
    ADDRESS_STATE_ID,
    ADDRESS_BUILD_TYPE_ID,
    PREVIOUS_ADDRESS_STATE_ID,
    ALTERNATIVE_ADDRESS_STATE_ID,
    ALTERNATIVE_ADDRESS_BUILD_TYPE_ID,
    JOB_REFERENCE_PROFESSIONAL_OCUPATION_ID,
    JOB_REFERENCE_PROFESSION_ID,
    JOB_REFERENCE_COMPANY_ACTIVITY_ID,
    JOB_REFERENCE_ANOTHER_INCOME_TYPE_ID,
    JOB_REFERENCE_ADDRESS_STATE_ID,

    PREVIOUS_FUNDING_BANK_ID,
    BANK_REFERENCE_BANK_ID,

    PERSONAL_REFERENCE_RELATIONSHIP,
    PROPERTY,

    PARTNER_ROLE,
    PARTNER_MARITAL_STATUS_ID,

    NATIONALITY,

    MONTHLY_BILLINGS_1_BILLING_CENTS as B1CENTS,
    MONTHLY_BILLINGS_1_MONTH as B1MONTH,
    MONTHLY_BILLINGS_1_YEAR as B1YEAR,

    MONTHLY_BILLINGS_2_BILLING_CENTS as B2CENTS,
    MONTHLY_BILLINGS_2_MONTH as B2MONTH,
    MONTHLY_BILLINGS_2_YEAR as B2YEAR,

    MONTHLY_BILLINGS_3_BILLING_CENTS as B3CENTS,
    MONTHLY_BILLINGS_3_MONTH as B3MONTH,
    MONTHLY_BILLINGS_3_YEAR as B3YEAR,

    MONTHLY_BILLINGS_4_BILLING_CENTS as B4CENTS,
    MONTHLY_BILLINGS_4_MONTH as B4MONTH,
    MONTHLY_BILLINGS_4_YEAR as B4YEAR,

    MONTHLY_BILLINGS_5_BILLING_CENTS as B5CENTS,
    MONTHLY_BILLINGS_5_MONTH as B5MONTH,
    MONTHLY_BILLINGS_5_YEAR as B5YEAR,

    MONTHLY_BILLINGS_6_BILLING_CENTS as B6CENTS,
    MONTHLY_BILLINGS_6_MONTH as B6MONTH,
    MONTHLY_BILLINGS_6_YEAR as B6YEAR,

    MONTHLY_BILLINGS_7_BILLING_CENTS as B7CENTS,
    MONTHLY_BILLINGS_7_MONTH as B7MONTH,
    MONTHLY_BILLINGS_7_YEAR as B7YEAR,

    MONTHLY_BILLINGS_8_BILLING_CENTS as B8CENTS,
    MONTHLY_BILLINGS_8_MONTH as B8MONTH,
    MONTHLY_BILLINGS_8_YEAR as B8YEAR,

    MONTHLY_BILLINGS_9_BILLING_CENTS as B9CENTS,
    MONTHLY_BILLINGS_9_MONTH as B9MONTH,
    MONTHLY_BILLINGS_9_YEAR as B9YEAR,

    MONTHLY_BILLINGS_10_BILLING_CENTS as B10CENTS,
    MONTHLY_BILLINGS_10_MONTH as B10MONTH,
    MONTHLY_BILLINGS_10_YEAR as B10YEAR,

    MONTHLY_BILLINGS_11_BILLING_CENTS as B11CENTS,
    MONTHLY_BILLINGS_11_MONTH as B11MONTH,
    MONTHLY_BILLINGS_11_YEAR as B11YEAR,

    MONTHLY_BILLINGS_12_BILLING_CENTS as B12CENTS,
    MONTHLY_BILLINGS_12_MONTH as B12MONTH,
    MONTHLY_BILLINGS_12_YEAR as B12YEAR,
  } from './Form/fields';

  export default {
    name: 'CustomersSummary',
    components: {
      FormSummary,
    },
    mixins: [
      preload,
    ],
    props: {
      collapsed: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        schemaGenerator,
        payloadGenerator,
      };
    },
    computed: {
      ...mapState([
        'customers',
        'domainsWeb',
        'banksWeb',
      ]),

      customer() {
        const customer = this.customers.read.item;
        const cellphone = customer.phones.find(p => p.phone_type.name === 'Celular');
        const alternative = customer.phones.find(p => p.phone_type.name === 'Principal');

        customer.phones = [cellphone, alternative].filter(i => i);

        return customer;
      },
      pf() {
        return !!this.customer.cpf;
      },
    },
    methods: {
      initialize() {
        this.skips();
        this.kinds();
        this.visibility();
        this.preload();
      },
      skips() {
        const { summary } = this.$refs;
        const { fields } = summary;

        Object
          .keys(fields)
          .filter((id) => {
            const { kind } = fields[id].data || {};

            if (!kind) {
              return false;
            }

            return !kind.includes(this.pf ? 'cpf' : 'cnpj');
          })
          .forEach(id => summary.updateVisible(id, false));
      },
      kinds() {
        const { summary } = this.$refs;
        const { fields } = summary;

        Object
          .keys(fields)
          .forEach((id) => {
            const { labels } = fields[id].data || {};

            if (labels) {
              summary.updateLabel(id, this.pf ? labels.cpf : labels.cnpj);
            }
          });

        if (this.pf) {
          return;
        }

        this.billing(B1CENTS, B1MONTH, B1YEAR);
        this.billing(B2CENTS, B2MONTH, B2YEAR);
        this.billing(B3CENTS, B3MONTH, B3YEAR);
        this.billing(B4CENTS, B4MONTH, B4YEAR);
        this.billing(B5CENTS, B5MONTH, B5YEAR);
        this.billing(B6CENTS, B6MONTH, B6YEAR);
        this.billing(B7CENTS, B7MONTH, B7YEAR);
        this.billing(B8CENTS, B8MONTH, B8YEAR);
        this.billing(B9CENTS, B9MONTH, B9YEAR);
        this.billing(B10CENTS, B10MONTH, B10YEAR);
        this.billing(B11CENTS, B11MONTH, B11YEAR);
        this.billing(B12CENTS, B12MONTH, B12YEAR);
      },
      billing(cents, month, year) {
        const { summary } = this.$refs;

        summary.updateLabel(
          cents,
          momentize(`${summary.fields[year].value}-${summary.fields[month].value}-01`, {
            output: 'MMMM [de] YYYY',
          }),
        );
      },
      visibility() {
        const { summary } = this.$refs;

        if (this.collapsed) {
          Object
            .keys(summary.sections)
            .forEach(section => summary.updateSectionToggled(section, true));
        }
      },
      preload() {
        Promise.all([
          this.preloadDomainsWeb(),
          this.preloadBanksWeb(),
        ]).then(this.populate);
      },
      populate() {
        if (!this.$refs.summary) {
          return;
        }

        const domains = this.domainsWeb.list.items;
        const banks = this.banksWeb.list.items;

        this.options(GENRE_ID, domains.genre);
        this.options(EDUCATION_ID, domains.education);
        this.options(COMPANY_ACTIVITY_ID, domains.company_activity);
        this.options(STATE_OF_BIRTH_ID, domains.states);
        this.options(RG_STATE_ID, domains.states);
        this.options(CNH_TYPE_ID, domains.cnh_type);
        this.options(MARITAL_STATUS_ID, domains.marital_status);
        this.options(ADDRESS_STATE_ID, domains.states);
        this.options(ADDRESS_BUILD_TYPE_ID, domains.build_type);
        this.options(PREVIOUS_ADDRESS_STATE_ID, domains.states);
        this.options(ALTERNATIVE_ADDRESS_STATE_ID, domains.states);
        this.options(ALTERNATIVE_ADDRESS_BUILD_TYPE_ID, domains.build_type);
        this.options(JOB_REFERENCE_PROFESSIONAL_OCUPATION_ID, domains.professional_ocupation);
        this.options(JOB_REFERENCE_PROFESSION_ID, domains.profession);
        this.options(JOB_REFERENCE_COMPANY_ACTIVITY_ID, domains.company_activity);
        this.options(JOB_REFERENCE_ANOTHER_INCOME_TYPE_ID, domains.another_income_type);
        this.options(JOB_REFERENCE_ADDRESS_STATE_ID, domains.states);

        this.options(PERSONAL_REFERENCE_RELATIONSHIP, domains.relationship);
        this.options(PROPERTY, domains.property);

        this.options(PARTNER_ROLE, domains.partner_role);
        this.options(PARTNER_MARITAL_STATUS_ID, domains.marital_status);

        this.options(NATIONALITY, domains.nationality);

        this.options(PREVIOUS_FUNDING_BANK_ID, banks);
        this.options(BANK_REFERENCE_BANK_ID, banks);
      },
      options(field, domains) {
        this.$refs.summary.updateOptions(
          field,
          Object
            .values(domains)
            .map(o => ({ label: o.name, value: o.id })),
        );
      },
    },
  };
</script>

<style lang="scss">
  .customers-summary {
    .state--collapsed .cordel-typography-subtitle {
      display: none;
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .customers-summary {
  }
</style>
